package com.arena_web.data.websocket

import com.arena_web.core.config.AppConfig
import com.arena_web.core.logger.getLogger
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.data.dto.EventMediaDto
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.data.dto.EventSubstitutionDto
import com.arena_web.data.dto.EventTeamDto
import com.arena_web.data.dto.EventTimerDto
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.dom.WebSocket
import org.w3c.dom.events.Event

sealed class EventWsEvent {
    data class ScoreUpdate(val eventTeamId: String, val name: String, val score: Int) : EventWsEvent()
    data class TimerUpdate(val timer: EventTimerDto) : EventWsEvent()
    data class StatUpdate(val targetUserId: String?, val eventTeamId: String?) : EventWsEvent()
    data class RatingReceived(val ratedUserId: String, val reviewerId: String, val generalRating: String) :
        EventWsEvent()
    data class MediaUploaded(val media: EventMediaDto) : EventWsEvent()
    data class MediaDeleted(val mediaId: String) : EventWsEvent()
    data class Substitution(val substitution: EventSubstitutionDto) : EventWsEvent()
    /** Carries the full updated event so other clients can swap state without a follow-up GET. */
    data class EventUpdated(val event: EventDetailDto) : EventWsEvent()
    data object EventDeleted : EventWsEvent()
    /** Carries the full updated event so participant removals on
     *  `NEW → IN_PROGRESS` reach other clients without a follow-up GET. */
    data class StatusChanged(val event: EventDetailDto) : EventWsEvent()
    // Update events carry the full enriched participant so other clients can
    // apply a granular merge without refetching the whole event payload.
    data class ParticipantJoined(val participant: EventParticipantDto) : EventWsEvent()
    data class ParticipantLeft(val userId: String) : EventWsEvent()
    data class ParticipantConfirmed(val participant: EventParticipantDto) : EventWsEvent()
    data class ParticipantRejected(val userId: String) : EventWsEvent()
    data class ParticipantAdminChanged(val participant: EventParticipantDto) : EventWsEvent()
    data class TeamAssigned(val participant: EventParticipantDto) : EventWsEvent()
    data class TeamLeft(val participant: EventParticipantDto) : EventWsEvent()
    data class TeamAdded(val team: EventTeamDto) : EventWsEvent()
    data class TeamRemoved(val eventTeamId: String) : EventWsEvent()
    data class ExtendedStatsToggled(val enabled: Boolean) : EventWsEvent()
    data object Connected : EventWsEvent()
    data object Disconnected : EventWsEvent()
}

class EventWebSocketClient {
    private val logger = getLogger<EventWebSocketClient>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    private var webSocket: WebSocket? = null
    private var currentEventId: String? = null
    private var reconnectAttempts = 0
    private var reconnectTimerId: Int? = null
    private var intentionalDisconnect = false

    private val _events = MutableSharedFlow<EventWsEvent>(extraBufferCapacity = 64)
    val events: SharedFlow<EventWsEvent> = _events.asSharedFlow()

    fun connect(eventId: String) {
        disconnect()
        intentionalDisconnect = false
        currentEventId = eventId

        val token = Storage.get(StorageKeys.ACCESS_TOKEN) ?: run {
            logger.error("No access token for Event WebSocket connection")
            return
        }

        val url = "${AppConfig.wsUrl}/events/$eventId?token=$token"
        logger.debug("Event WS connecting to $url")

        try {
            val ws = WebSocket(url)

            ws.onopen = {
                logger.debug("Event WS connected to event $eventId")
                reconnectAttempts = 0
                _events.tryEmit(EventWsEvent.Connected)
                Unit
            }

            ws.onmessage = { event: Event ->
                val msgEvent = event.asDynamic()
                val text = msgEvent.data as? String
                if (text != null) {
                    handleMessage(text)
                }
                Unit
            }

            ws.onclose = {
                logger.debug("Event WS disconnected from event $eventId")
                _events.tryEmit(EventWsEvent.Disconnected)
                if (!intentionalDisconnect) {
                    scheduleReconnect()
                }
                Unit
            }

            ws.onerror = {
                logger.error("Event WS error on event $eventId")
                Unit
            }

            webSocket = ws
        } catch (e: Exception) {
            logger.error("Failed to create Event WebSocket: ${e.message}")
        }
    }

    fun disconnect() {
        intentionalDisconnect = true
        reconnectTimerId?.let { kotlinx.browser.window.clearTimeout(it) }
        reconnectTimerId = null
        reconnectAttempts = 0
        webSocket?.close()
        webSocket = null
        currentEventId = null
    }

    private fun scheduleReconnect() {
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            logger.error("Event WS max reconnect attempts reached")
            return
        }
        val eventId = currentEventId ?: return
        val delayMs = RECONNECT_DELAYS.getOrElse(reconnectAttempts) { RECONNECT_DELAYS.last() }
        reconnectAttempts++
        logger.debug("Event WS reconnecting in ${delayMs}ms (attempt $reconnectAttempts)")

        reconnectTimerId = kotlinx.browser.window.setTimeout({
            reconnectTimerId = null
            if (!intentionalDisconnect) {
                connect(eventId)
            }
        }, delayMs)
    }

    @Suppress("LongMethod", "CyclomaticComplexMethod")
    private fun handleMessage(text: String) {
        try {
            val jsonElement = json.parseToJsonElement(text)
            val obj = jsonElement.jsonObject
            val type = obj["type"]?.jsonPrimitive?.content ?: return
            val data = obj["data"] ?: return

            val event = when (type) {
                "SCORE_UPDATE" -> {
                    val d = data.jsonObject
                    EventWsEvent.ScoreUpdate(
                        eventTeamId = d["eventTeamId"]?.jsonPrimitive?.content ?: return,
                        name = d["name"]?.jsonPrimitive?.content ?: "",
                        score = d["score"]?.jsonPrimitive?.content?.toIntOrNull() ?: 0,
                    )
                }
                "TIMER_UPDATE" -> EventWsEvent.TimerUpdate(
                    json.decodeFromJsonElement(EventTimerDto.serializer(), data)
                )
                "STAT_UPDATE" -> {
                    val d = data.jsonObject
                    EventWsEvent.StatUpdate(
                        targetUserId = d["targetUserId"]?.jsonPrimitive?.content,
                        eventTeamId = d["eventTeamId"]?.jsonPrimitive?.content,
                    )
                }
                "RATING_RECEIVED" -> {
                    val d = data.jsonObject
                    EventWsEvent.RatingReceived(
                        ratedUserId = d["ratedUserId"]?.jsonPrimitive?.content ?: return,
                        reviewerId = d["reviewerId"]?.jsonPrimitive?.content ?: return,
                        generalRating = d["generalRating"]?.jsonPrimitive?.content ?: "0",
                    )
                }
                "MEDIA_UPLOADED" -> EventWsEvent.MediaUploaded(
                    json.decodeFromJsonElement(EventMediaDto.serializer(), data)
                )
                "MEDIA_DELETED" -> EventWsEvent.MediaDeleted(
                    data.jsonObject["mediaId"]?.jsonPrimitive?.content ?: return
                )
                "SUBSTITUTION" -> EventWsEvent.Substitution(
                    json.decodeFromJsonElement(EventSubstitutionDto.serializer(), data)
                )
                "EVENT_UPDATED" -> EventWsEvent.EventUpdated(
                    json.decodeFromJsonElement(EventDetailDto.serializer(), data)
                )
                "EVENT_DELETED" -> EventWsEvent.EventDeleted
                "EVENT_STATUS_CHANGED" -> EventWsEvent.StatusChanged(
                    json.decodeFromJsonElement(EventDetailDto.serializer(), data)
                )
                "PARTICIPANT_JOINED" -> EventWsEvent.ParticipantJoined(
                    json.decodeFromJsonElement(EventParticipantDto.serializer(), data)
                )
                "PARTICIPANT_LEFT" -> EventWsEvent.ParticipantLeft(
                    data.jsonObject["userId"]?.jsonPrimitive?.content ?: return
                )
                "PARTICIPANT_CONFIRMED" -> EventWsEvent.ParticipantConfirmed(
                    json.decodeFromJsonElement(EventParticipantDto.serializer(), data)
                )
                "PARTICIPANT_REJECTED" -> EventWsEvent.ParticipantRejected(
                    data.jsonObject["userId"]?.jsonPrimitive?.content ?: return
                )
                "PARTICIPANT_ADMIN_CHANGED" -> EventWsEvent.ParticipantAdminChanged(
                    json.decodeFromJsonElement(EventParticipantDto.serializer(), data)
                )
                "TEAM_ASSIGNED" -> EventWsEvent.TeamAssigned(
                    json.decodeFromJsonElement(EventParticipantDto.serializer(), data)
                )
                "TEAM_LEFT" -> EventWsEvent.TeamLeft(
                    json.decodeFromJsonElement(EventParticipantDto.serializer(), data)
                )
                "TEAM_ADDED" -> EventWsEvent.TeamAdded(
                    json.decodeFromJsonElement(EventTeamDto.serializer(), data)
                )
                "TEAM_REMOVED" -> EventWsEvent.TeamRemoved(
                    data.jsonObject["eventTeamId"]?.jsonPrimitive?.content ?: return
                )
                "EXTENDED_STATS_TOGGLED" -> EventWsEvent.ExtendedStatsToggled(
                    data.jsonObject["enabled"]?.jsonPrimitive?.content?.toBoolean() ?: false
                )
                else -> {
                    logger.debug("Unknown Event WS type: $type")
                    null
                }
            }

            if (event != null) {
                _events.tryEmit(event)
            }
        } catch (e: Exception) {
            logger.error("Failed to parse Event WS message: ${e.message}")
        }
    }

    companion object {
        private const val MAX_RECONNECT_ATTEMPTS = 5
        private val RECONNECT_DELAYS = listOf(1000, 2000, 3000, 3000, 3000)
    }
}
